import { ART_FORMATS, ASPECT_RATIOS, ID_PREFIX, TEXT_BINDINGS, formatRatios, isPlaceholderSource, newId, normalizeLayer } from './schema';
import { layoutText, textHeight } from './textLayout';
import { boxBounds, layerBox } from './geometry';
import { fontEntry, nearestWeight } from './fonts';

// Immutable document operations: each returns a new document and keeps every untouched
// page and layer by reference, so history snapshots stay cheap and memoized views of an
// untouched layer do not render again.
export const pageOf = (doc, pageId) => doc.pages.find(page => page.id === pageId) || doc.pages[0];

export function findLayer(doc, layerId) {
  for (const page of doc.pages) {
    const index = page.layers.findIndex(layer => layer.id === layerId);
    if (index >= 0) return { page, layer: page.layers[index], index };
  }
  return null;
}

const mapPage = (doc, pageId, fn) => ({ ...doc, pages: doc.pages.map(page => (page.id === pageId ? fn(page) : page)) });
const mapLayers = (doc, ids, fn) => {
  const wanted = new Set(ids);
  return { ...doc, pages: doc.pages.map(page => (page.layers.some(layer => wanted.has(layer.id))
    ? { ...page, layers: page.layers.map(layer => (wanted.has(layer.id) ? fn(layer) : layer)) } : page)) };
};

// A text box's height follows its lines whenever its text, style or width moves.
export const withTextHeight = layer => (layer.type === 'text' ? { ...layer, height: Math.round(textHeight(layer) * 100) / 100 } : layer);

// A readable layout adjustment instead of a cut: the font shrinks (never below `minScale`
// of its size) until the lines fit `maxHeight`. `fits` says whether it got there.
export function fitTextToHeight(layer, maxHeight, { minScale = 0.55 } = {}) {
  const current = withTextHeight(layer);
  if (layer.type !== 'text' || current.height <= maxHeight) return { layer: current, fits: true };
  const sized = size => withTextHeight({ ...layer, style: { ...layer.style, font_size: Math.floor(size) } });
  let low = Math.max(8, layer.style.font_size * minScale);
  let high = layer.style.font_size;
  let best = null;
  for (let step = 0; step < 14; step++) {
    const mid = (low + high) / 2;
    const candidate = sized(mid);
    if (candidate.height <= maxHeight) { best = candidate; low = mid; } else high = mid;
  }
  const result = best || sized(Math.max(8, layer.style.font_size * minScale));
  return { layer: result, fits: result.height <= maxHeight };
}

// A label (a pill, a call to action) hugging its text on one line, within `maxWidth`,
// keeping the edge (or center) its alignment anchors.
export function hugTextWidth(layer, maxWidth) {
  if (layer.type !== 'text') return layer;
  const layout = layoutText(layer.text, layer.style, 100000);
  const padding = layer.style.highlight ? layer.style.highlight.padding : 0;
  const width = Math.max(24, Math.min(maxWidth, Math.ceil(Math.max(0, ...layout.lines.map(line => line.width)) + padding * 2 + 2)));
  const x = layer.style.align === 'center' ? layer.x + (layer.width - width) / 2 : layer.style.align === 'right' ? layer.x + layer.width - width : layer.x;
  return withTextHeight({ ...layer, width, x: Math.round(x) });
}
const isLabel = layer => layer.type === 'text' && layer.style.highlight?.mode === 'box' && !layer.text.includes('\n');

// Merges a patch into a layer; `style` and `adjust` merge one level deep.
export function patchLayer(layer, patch) {
  const next = { ...layer, ...patch };
  if (patch.style) next.style = { ...layer.style, ...patch.style };
  if (patch.adjust) next.adjust = { ...layer.adjust, ...patch.adjust };
  return withTextHeight(next);
}

export const updateLayer = (doc, layerId, patch) => mapLayers(doc, [layerId], layer => patchLayer(layer, patch));
export const updateLayers = (doc, ids, patch) => mapLayers(doc, ids, layer => patchLayer(layer, typeof patch === 'function' ? patch(layer) : patch));

export function addLayer(doc, pageId, layer, index = null) {
  return mapPage(doc, pageOf(doc, pageId).id, page => {
    const layers = page.layers.slice();
    layers.splice(index === null ? layers.length : index, 0, withTextHeight(layer));
    return { ...page, layers };
  });
}

export function removeLayers(doc, ids) {
  const wanted = new Set(ids);
  return { ...doc, pages: doc.pages.map(page => (page.layers.some(layer => wanted.has(layer.id)) ? { ...page, layers: page.layers.filter(layer => !wanted.has(layer.id)) } : page)) };
}

// A copy above the original, nudged down-right so it shows; returns the new id too.
export function duplicateLayer(doc, layerId, offset = 32) {
  const found = findLayer(doc, layerId);
  if (!found) return { doc, id: null };
  const copy = { ...found.layer, id: newId(ID_PREFIX.layer), x: found.layer.x + offset, y: found.layer.y + offset, locked: false, name: found.layer.name };
  return { doc: addLayer(doc, found.page.id, copy, found.index + 1), id: copy.id };
}

// Pastes layers copied from any document (a fresh id each), centered on where they were.
export function pasteLayers(doc, pageId, layers, offset = 32) {
  let next = doc;
  const ids = [];
  layers.forEach(source => {
    const layer = normalizeLayer({ ...source, id: newId(ID_PREFIX.layer), x: source.x + offset, y: source.y + offset, locked: false });
    if (!layer) return;
    next = addLayer(next, pageId, layer);
    ids.push(layer.id);
  });
  return { doc: next, ids };
}

// Moves a layer in its page's paint order (0 = bottom).
export function reorderLayer(doc, layerId, toIndex) {
  const found = findLayer(doc, layerId);
  if (!found) return doc;
  return mapPage(doc, found.page.id, page => {
    const layers = page.layers.slice();
    const [layer] = layers.splice(found.index, 1);
    layers.splice(Math.max(0, Math.min(layers.length, toIndex)), 0, layer);
    return { ...page, layers };
  });
}

export function arrangeLayer(doc, layerId, where) {
  const found = findLayer(doc, layerId);
  if (!found) return doc;
  const last = found.page.layers.length - 1;
  const target = { forward: found.index + 1, backward: found.index - 1, front: last, back: 0 }[where];
  return target === undefined ? doc : reorderLayer(doc, layerId, target);
}

// Aligns a layer to the page by its visible (rotated) bounds.
export function alignLayer(doc, layerId, edge) {
  const found = findLayer(doc, layerId);
  if (!found) return doc;
  const bounds = boxBounds(layerBox(found.layer));
  const { width, height } = doc;
  const delta = {
    left: { x: -bounds.left }, center: { x: width / 2 - bounds.cx }, right: { x: width - bounds.right },
    top: { y: -bounds.top }, middle: { y: height / 2 - bounds.cy }, bottom: { y: height - bounds.bottom },
  }[edge];
  if (!delta) return doc;
  return updateLayer(doc, layerId, { x: Math.round(found.layer.x + (delta.x || 0)), y: Math.round(found.layer.y + (delta.y || 0)) });
}

export const setPageBackground = (doc, pageId, background) => mapPage(doc, pageOf(doc, pageId).id, page => ({ ...page, background }));

// A layer that covers the whole page (a background photo or color block) stays full-bleed.
export const coversPage = (layer, size) => !layer.rotation && layer.x <= 1 && layer.y <= 1 && layer.x + layer.width >= size.width - 1 && layer.y + layer.height >= size.height - 1;

// Where a background video goes in a page's layers (2.13.0): above the page's own background
// (the run of opaque full-page photos, color blocks and earlier background videos at the
// bottom), under what is laid over it (a shade or a see-through block that darkens it, texts).
export function backgroundIndex(page, size) {
  let index = 0;
  while (index < page.layers.length) {
    const layer = page.layers[index];
    const seeThrough = layer.opacity < 1 || (layer.fill?.type === 'solid' && (layer.fill.opacity ?? 1) < 1);
    if (layer.type === 'text' || !coversPage(layer, size) || isShade(layer) || seeThrough) break;
    index += 1;
  }
  return index;
}

// Changes the aspect ratio: full-bleed layers stretch to the new page, every other layer
// keeps its size and its center moves proportionally, so the composition keeps its rhythm.
export function setAspectRatio(doc, ratio) {
  if (!formatRatios(doc.format).includes(ratio) || ratio === doc.aspect_ratio) return doc;
  const size = ASPECT_RATIOS[ratio];
  const sx = size.width / doc.width;
  const sy = size.height / doc.height;
  const pages = doc.pages.map(page => ({ ...page, layers: page.layers.map(layer => {
    if (layer.type !== 'text' && coversPage(layer, doc)) return { ...layer, x: 0, y: 0, width: size.width, height: size.height };
    const box = layerBox(layer);
    const cx = (box.x + box.width / 2) * sx;
    const cy = (box.y + box.height / 2) * sy;
    return { ...layer, x: Math.round(cx - box.width / 2), y: Math.round(cy - box.height / 2) };
  }) }));
  return { ...doc, aspect_ratio: ratio, width: size.width, height: size.height, pages };
}

// Every image source of the document (image layers, and shapes and texts painted with a
// photo) through `fn`, which answers the source to keep in its place.
export function mapImageSources(doc, fn) {
  const paint = fill => (fill?.type === 'image' ? { ...fill, source: fn(fill.source) } : fill);
  return { ...doc, pages: doc.pages.map(page => ({ ...page, layers: page.layers.map(layer => {
    if (layer.type === 'image') return { ...layer, source: fn(layer.source) };
    if (layer.type === 'shape' && layer.fill?.type === 'image') return { ...layer, fill: paint(layer.fill) };
    if (layer.type === 'text' && layer.style?.fill?.type === 'image') return { ...layer, style: { ...layer.style, fill: paint(layer.style.fill) } };
    return layer;
  }) })) };
}

export function replaceImage(doc, layerId, source) {
  return updateLayer(doc, layerId, { source, focus_x: 0.5, focus_y: 0.5, zoom: 1 });
}

const NEUTRAL_ADJUST = { brightness: 0, contrast: 0, saturation: 0, blur: 0, grayscale: false };
const photoPaint = (source, previous) => ({ type: 'image', source, fit: previous?.fit || 'cover', focus_x: 0.5, focus_y: 0.5, zoom: 1, adjust: previous?.adjust || NEUTRAL_ADJUST });

// A shape filled with a photo (or with another photo), clipped to its outline and framed
// from the center.
export function setShapeImage(doc, layerId, source) {
  const found = findLayer(doc, layerId);
  return updateLayer(doc, layerId, { fill: photoPaint(source, found?.layer.fill?.type === 'image' ? found.layer.fill : null) });
}

// A photo into whatever takes one: the photo of an image layer, the fill of a shape, the
// paint of a text (masked by its letters). Fit and adjustments carry over.
export function setImageFill(doc, layerId, source) {
  const layer = findLayer(doc, layerId)?.layer;
  if (!layer) return doc;
  if (layer.type === 'image') return replaceImage(doc, layerId, source);
  if (layer.type === 'shape') return setShapeImage(doc, layerId, source);
  if (layer.type === 'text') return updateLayer(doc, layerId, { style: { fill: photoPaint(source, layer.style.fill?.type === 'image' ? layer.style.fill : null) } });
  return doc;
}

// A shade: a decoration painted with a black gradient, laid over a photo so the text on it
// reads (core's ArtImagePlacement::isShade). Never a place for a photo itself.
export const isShade = layer => layer?.type === 'shape' && layer.binding === 'decoration' && ['linear', 'radial'].includes(layer.fill?.type)
  && (layer.fill.stops || []).every(stop => String(stop.color || '').toLowerCase() === '#000000');

// The layer a photo action is about. A photo frame with text on it carries its shade right
// above, unlocked and the same shape and box (±1, core's isShadeOf), so a click on the photo
// lands on the shade: that resolves to the photo-filled frame below. Any other layer is itself.
export function photoFrameOf(page, layer) {
  if (!page || !isShade(layer)) return layer;
  const index = page.layers.findIndex(item => item.id === layer.id);
  const below = index > 0 ? page.layers[index - 1] : null;
  const covered = below?.type === 'shape' && below.shape === layer.shape && below.fill?.type === 'image'
    && ['x', 'y', 'width', 'height'].every(key => Math.abs(below[key] - layer[key]) < 1);
  return covered ? below : layer;
}

const HEADING_BINDINGS = ['headline', 'subheadline', 'quote', 'stat', 'badge'];
// Contract 2.10.0: the heading bindings a kit's subheading style takes over, when it has one.
const SUBHEADING_BINDINGS = ['subheadline', 'badge'];
// The brand kit's text style for a binding (core's ArtLayout::fontFor): the subheading's for
// subheadings and badges (the heading's when the kit has none), the heading's for the other
// heading bindings, the body's for the rest. `role` picks one by name instead.
export function brandStyleFor(fonts, binding, role = null) {
  if (!fonts) return null;
  if (role) return fonts[role] || (role === 'subheading' ? fonts.heading : null) || fonts.body || fonts.heading || null;
  if (SUBHEADING_BINDINGS.includes(binding)) return fonts.subheading || fonts.heading || fonts.body || null;
  return HEADING_BINDINGS.includes(binding) ? fonts.heading || fonts.body || null : fonts.body || fonts.heading || null;
}
// A text style of the kit on a text's style: the face and its weight (a chat edit's fonts
// carry no weight: the nearest to the text's own), and, 2.10.0, italic, case, line height and
// letter spacing when the kit sets them (null keeps the template's).
export function withBrandStyle(style, font) {
  const family = fontEntry(font.family).family;
  const next = { ...style, font_family: family, font_weight: nearestWeight(family, Number.isInteger(font.weight) ? font.weight : style.font_weight) };
  if (typeof font.italic === 'boolean') next.italic = font.italic;
  if (['none', 'uppercase'].includes(font.transform)) next.transform = font.transform;
  if (Number.isFinite(font.line_height)) next.line_height = font.line_height;
  if (Number.isFinite(font.letter_spacing)) next.letter_spacing = font.letter_spacing;
  return next;
}
// The brand's text styles on every text layer, by binding.
export function applyBrandFonts(doc, fonts) {
  if (!fonts?.heading && !fonts?.body) return doc;
  return { ...doc, pages: doc.pages.map(page => ({ ...page, layers: page.layers.map(layer => {
    if (layer.type !== 'text') return layer;
    return withTextHeight({ ...layer, style: withBrandStyle(layer.style, brandStyleFor(fonts, layer.binding)) });
  }) })) };
}

// Swaps the design for a template, keeping the content: each bound text of the template
// takes the current text of the same binding, in order where a binding repeats (speech
// bubbles, callouts), bound photos keep the creator's image. The
// result has the template's layout at the current aspect ratio; a moved text that is longer
// than the template's own shrinks into its slot, and labels hug their new text. Page by
// page, and the page count stays: the first page takes the template's cover, the last its
// closing page, the ones between its middle pages in turn.
export function applyTemplate(doc, template) {
  const fitted = setAspectRatio({ ...template, format: doc.format, aspect_ratio: template.aspect_ratio }, doc.aspect_ratio);
  const designs = fitted.pages;
  const middle = designs.slice(1, -1);
  const designFor = index => {
    if (index === 0 || designs.length === 1) return designs[0];
    if (index === doc.pages.length - 1) return designs[designs.length - 1];
    return middle.length ? middle[(index - 1) % middle.length] : designs[designs.length - 1];
  };
  const pages = doc.pages.map((page, index) => {
    const texts = new Map();
    const images = new Map();
    page.layers.forEach(layer => {
      if (layer.type === 'text' && TEXT_BINDINGS.includes(layer.binding)) texts.set(layer.binding, [...(texts.get(layer.binding) || []), layer.text]);
      if (layer.type === 'image' && layer.binding && !images.has(layer.binding)) images.set(layer.binding, layer.source);
    });
    const source = designFor(index);
    const taken = new Map();
    const textFor = layer => {
      if (layer.type !== 'text' || !texts.has(layer.binding)) return undefined;
      const nth = taken.get(layer.binding) || 0;
      taken.set(layer.binding, nth + 1);
      return texts.get(layer.binding)[nth];
    };
    const designed = {
      ...source,
      id: page.id,
      layers: source.layers.map(layer => {
        const fresh = { ...layer, id: newId(ID_PREFIX.layer) };
        const text = textFor(layer);
        if (text !== undefined) {
          const moved = { ...fresh, text };
          if (isLabel(moved)) return hugTextWidth(moved, fitted.width - Math.max(0, moved.x) - 40);
          return fitTextToHeight(moved, textHeight(layer)).layer;
        }
        if (layer.type === 'image' && images.has(layer.binding)) return { ...fresh, source: images.get(layer.binding), focus_x: 0.5, focus_y: 0.5, zoom: 1 };
        return withTextHeight(fresh);
      }),
    };
    return withVideosOf(page, designed, fitted);
  });
  return { ...fitted, pages };
}

// 2.13.0: a page's videos survive a new design: its background clips over the design's own
// background, the others on top, in their order.
function withVideosOf(page, designed, size) {
  const videos = page.layers.filter(layer => layer.type === 'video');
  if (!videos.length) return designed;
  const background = videos.filter(layer => coversPage(layer, size));
  const layers = designed.layers.slice();
  layers.splice(backgroundIndex(designed, size), 0, ...background);
  return { ...designed, layers: [...layers, ...videos.filter(layer => !background.includes(layer))] };
}

// The document without its videos (a personal template keeps the design, not the clips).
export const withoutVideos = doc => ({ ...doc, pages: doc.pages.map(page => (page.layers.some(layer => layer.type === 'video')
  ? { ...page, layers: page.layers.filter(layer => layer.type !== 'video') } : page)) });

// ── pages (a carousel's) ──
export const maxPages = doc => ART_FORMATS[doc.format]?.maxPages || 1;
export const canAddPage = (doc, count = 1) => doc.pages.length + count <= maxPages(doc);
export const pageIndex = (doc, pageId) => Math.max(0, doc.pages.findIndex(page => page.id === pageId));
const withFreshIds = page => ({ ...page, id: newId(ID_PREFIX.page), layers: page.layers.map(layer => ({ ...layer, id: newId(ID_PREFIX.layer) })) });

// An empty page that keeps the carousel's look: the background of the page it follows.
export const blankPage = (doc, after = null) => ({ id: newId(ID_PREFIX.page), name: null, background: (after || doc.pages[0])?.background || { type: 'solid', color: '#ffffff' }, layers: [] });

// A template's pages ready to go into `doc`: at its aspect ratio, with new ids, as many as fit.
export function templatePages(doc, template) {
  const fitted = setAspectRatio({ ...template, format: doc.format, aspect_ratio: template.aspect_ratio }, doc.aspect_ratio);
  return fitted.pages.slice(0, Math.max(0, maxPages(doc) - doc.pages.length)).map(page => {
    const fresh = withFreshIds(page);
    return { ...fresh, name: null, layers: fresh.layers.map(withTextHeight) };
  });
}

// A page model (contract 2.10.0, the catalog's static posts) as new pages of `doc`: at its
// ratio with new ids, in the brand kit's text styles (`fonts`, a configured kit's), the
// creator's picture in its empty avatar places (`avatar`, an image source) and their @ in its
// handles (`handle`).
export function modelPages(doc, template, { fonts = null, avatar = null, handle = null } = {}) {
  const pages = templatePages(doc, template);
  const styled = fonts ? applyBrandFonts({ ...doc, pages }, fonts).pages : pages;
  return styled.map(page => ({ ...page, layers: page.layers.map(layer => {
    if (avatar && layer.type === 'image' && layer.binding === 'avatar' && isPlaceholderSource(layer.source)) return { ...layer, source: avatar, focus_x: 0.5, focus_y: 0.5, zoom: 1 };
    if (handle && layer.type === 'text' && layer.binding === 'handle') return withTextHeight({ ...layer, text: handle });
    return layer;
  }) }));
}

// The empty image places of a document (2.10.0): image layers and photo-filled shapes still
// showing a placeholder, each with its page. The download warns about them.
export function emptyImagePlaces(doc) {
  return (doc?.pages || []).flatMap(page => page.layers.filter(layer => layer.visible && (
    (layer.type === 'image' && isPlaceholderSource(layer.source)) || (layer.type === 'shape' && layer.fill?.type === 'image' && isPlaceholderSource(layer.fill.source))))
    .map(layer => ({ ...layer, pageId: page.id })));
}

// Pages in at `index` (the end by default), as many as the format still takes.
export function addPages(doc, pages, index = doc.pages.length) {
  const room = maxPages(doc) - doc.pages.length;
  if (room <= 0 || !pages.length) return doc;
  const at = Math.max(0, Math.min(index, doc.pages.length));
  return { ...doc, pages: [...doc.pages.slice(0, at), ...pages.slice(0, room), ...doc.pages.slice(at)] };
}

// A copy right after the original; `id` is the copy's.
export function duplicatePage(doc, pageId) {
  const index = doc.pages.findIndex(page => page.id === pageId);
  if (index < 0 || !canAddPage(doc)) return { doc, id: null };
  const copy = withFreshIds(doc.pages[index]);
  return { doc: addPages(doc, [copy], index + 1), id: copy.id };
}

// A document always keeps one page.
export function removePage(doc, pageId) {
  if (doc.pages.length < 2 || !doc.pages.some(page => page.id === pageId)) return doc;
  return { ...doc, pages: doc.pages.filter(page => page.id !== pageId) };
}

export function movePage(doc, pageId, index) {
  const from = doc.pages.findIndex(page => page.id === pageId);
  const to = Math.max(0, Math.min(index, doc.pages.length - 1));
  if (from < 0 || from === to) return doc;
  const pages = doc.pages.slice();
  const [page] = pages.splice(from, 1);
  pages.splice(to, 0, page);
  return { ...doc, pages };
}

// The text of each binding, for the chat and the caption ("the title says…").
export function boundTexts(doc) {
  const result = {};
  (doc?.pages || []).forEach(page => page.layers.forEach(layer => {
    if (layer.type === 'text' && layer.binding && !result[layer.binding]) result[layer.binding] = layer.text;
  }));
  return result;
}
